import React from 'react';
import isString from 'lodash/isString';
import isNil from 'lodash/isNil';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import get from 'lodash/get';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import { connect, getIn, FormikState } from 'formik';
import SurveyItem from '../../util/SurveyItem';
import { isRequiredValueEmpty } from '../../util/requiredValue';
import { RepeatingGroupInstanceContext } from '../Contexts';
import {
  injectIntl,
  WrappedComponentProps,
  MessageDescriptor,
} from 'react-intl';
import { dateFormatValidation } from '../../util/DateTimeParsers';
import fhirTypeCheck from '../../util/fhirTypeCheck';

const errorMessages = {
  required: {
    id: 'surveys.validation.required',
    defaultMessage: 'Field is required',
  },
  maxLength: (maxLength: number) => ({
    id: 'surveys.validation.maxLength',
    defaultMessage: `Value cannot be longer than ${maxLength} characters`,
  }),
  integerType: {
    id: 'surveys.validation.integerType',
    defaultMessage: 'Value should be a whole number',
  },
  decimalType: {
    id: 'surveys.validation.decimalType',
    defaultMessage: 'Value must be a decimal value',
  },
  nonNegative: {
    id: 'surveys.validation.notNegative',
    defaultMessage: 'Value cannot be negative',
  },
  invalidFormat: {
    id: 'surveys.validation.invalidFormat',
    defaultMessage: 'Invalid Format',
  },
  maxNumericBounds: {
    id: 'surveys.validation.maxNumericBounds',
    defaultMessage: `Value cannot be greater than {maxBound}`,
  },
  minNumericBounds: {
    id: 'surveys.validation.minNumericBounds',
    defaultMessage: `Value cannot be less than {minBound}`,
  },
  maxSelectionCountBounds: {
    id: 'surveys.validation.maxSelectionCountBounds',
    defaultMessage: `Cannot select more than {maxBound} {maxBound, plural, one {option} other {options}}`,
  },
  minSelectionCountBounds: {
    id: 'surveys.validation.minSelectionCountBounds',
    defaultMessage: `Cannot select less than {minBound} {minBound, plural, one {option} other {options}}`,
  },
  maxCharLength: {
    id: 'surveys.validation.maxCharLength',
    defaultMessage:
      'Value cannot be longer than {maxBound} {maxBound, plural, one {character} other {characters}}',
  },
  minCharLength: {
    id: 'surveys.validation.minCharLength',
    defaultMessage:
      'Value cannot be shorter than {minBound} {minBound, plural, one {character} other {characters}}',
  },
};

export interface WithValidationInjectedProps {
  validate: (value: any) => string;
  error: string;
  shouldShowError: boolean;
}

interface WrappedComponentType {
  surveyItem: SurveyItem;
  isEnabled: boolean;
  isReadOnly: boolean;
}

interface ConnectedFormik {
  formik: FormikState<any>;
}

const withValidation = <OtherProps extends {}>(
  WrappedComponent: React.ComponentType<
    WrappedComponentType & WithValidationInjectedProps & OtherProps
  >,
) => {
  class WithValidation extends React.PureComponent<
    WrappedComponentProps & ConnectedFormik & WrappedComponentType & OtherProps
  > {
    static contextType = RepeatingGroupInstanceContext;
    declare context: React.ContextType<typeof RepeatingGroupInstanceContext>;

    format = (intlMessage: MessageDescriptor) =>
      this.props.intl.formatMessage(intlMessage);

    /**
     * The formik field name for this item.  Inside a repeating group
     * instance, names (and therefore error/touched paths) are
     * instance-qualified, e.g. "sisters[1].sister-age"
     */
    getFieldName = () => {
      const link = this.props.surveyItem.link;
      return this.context ? `${this.context.namePrefix}${link}` : link;
    };

    requiredValidation = (value: any) => {
      // if not enabled, required doesn't apply
      if (!this.props.surveyItem.required || !this.props.isEnabled) return;

      if (isRequiredValueEmpty(value, this.props.surveyItem.type)) {
        return this.format(errorMessages.required);
      }
    };

    maxLengthValidation = (value: any) => {
      const maxLength = this.props.surveyItem.maxLength;
      if (!maxLength || !this.props.isEnabled || !isString(value)) return;

      const itemType = this.props.surveyItem.type;
      if (itemType !== 'string' && itemType !== 'url') return;

      if (value.length > maxLength) {
        return this.format(errorMessages.maxLength(maxLength));
      }
    };

    integerValidation = (value: any) => {
      if (this.props.surveyItem.type !== 'integer') return;

      if (!isNil(value) && !Number.isInteger(value)) {
        return this.format(errorMessages.integerType);
      }
    };

    decimalValidation = (value: any) => {
      if (this.props.surveyItem.type !== 'decimal') return;

      if (!isNil(value) && Number.isNaN(value)) {
        return this.format(errorMessages.decimalType);
      }
    };

    nonNegativeValidation = (value: any) => {
      if (
        this.props.surveyItem.type !== 'integer' &&
        this.props.surveyItem.type !== 'decimal'
      ) {
        return;
      }

      if (
        !this.props.surveyItem.nonNegative ||
        isNil(value) ||
        !isNumber(value) ||
        value >= 0
      ) {
        return;
      }

      return this.format(errorMessages.nonNegative);
    };

    dateTimeFormat = (value: any) => {
      return dateFormatValidation(
        value,
        this.props.surveyItem.type,
        this.props.intl.formatMessage,
      );
    };

    numericBoundsValidation = (value: any) => {
      const {
        surveyItem: {
          hasMinNumericBoundsValidation,
          hasMaxNumericBoundsValidation,
          maxNumericBounds,
          minNumericBounds,
          type,
        },
        intl: { formatMessage },
      } = this.props;

      if (
        isNil(value) ||
        (!hasMinNumericBoundsValidation && !hasMaxNumericBoundsValidation) ||
        !['integer', 'decimal', 'quantity'].includes(type)
      ) {
        return;
      }

      const exectuteValidation = (
        numericValue: number,
        fhirType: 'integer' | 'decimal' | 'quantity',
      ) => {
        if (isNil(numericValue) || !fhirTypeCheck(fhirType, value, false)) {
          return;
        }

        if (hasMinNumericBoundsValidation && numericValue < minNumericBounds) {
          return formatMessage(errorMessages.minNumericBounds, {
            minBound: minNumericBounds,
          });
        } else if (
          hasMaxNumericBoundsValidation &&
          numericValue > maxNumericBounds
        ) {
          return formatMessage(errorMessages.maxNumericBounds, {
            maxBound: maxNumericBounds,
          });
        }
      };

      if (type === 'quantity') {
        const quantityValue = get<fhir.Quantity, 'value', number>(
          value,
          'value',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          null,
        );
        return exectuteValidation(quantityValue, 'quantity');
      } else if (type === 'decimal') {
        return exectuteValidation(value, 'decimal');
      } else if (type === 'integer') {
        return exectuteValidation(value, 'integer');
      }
    };

    selectionCountValidation = (value: any) => {
      const {
        surveyItem: {
          hasMinSelectionCountBoundsValidation,
          hasMaxSelectionCountBoundsValidation,
          maxSelectionCountBounds,
          minSelectionCountBounds,
          type,
          repeats,
        },
        intl: { formatMessage },
      } = this.props;

      if (
        isNil(value) ||
        !isArray(value) ||
        (!hasMinSelectionCountBoundsValidation &&
          !hasMaxSelectionCountBoundsValidation) ||
        type !== 'choice' ||
        !repeats
      ) {
        return;
      }

      if (
        hasMaxSelectionCountBoundsValidation &&
        value.length > maxSelectionCountBounds
      ) {
        return formatMessage(errorMessages.maxSelectionCountBounds, {
          maxBound: maxSelectionCountBounds,
        });
      } else if (
        hasMinSelectionCountBoundsValidation &&
        value.length < minSelectionCountBounds
      ) {
        return formatMessage(errorMessages.minSelectionCountBounds, {
          minBound: minSelectionCountBounds,
        });
      } else {
        return;
      }
    };

    charLengthValidation = (value: any) => {
      const {
        surveyItem: {
          hasMaxCharCountValidation,
          hasMinCharCountValidation,
          maxCharCountBounds,
          minCharCountBounds,
          type,
        },
        intl: { formatMessage },
      } = this.props;

      if (
        type !== 'string' ||
        isNil(value) ||
        isEmpty(value) ||
        !fhirTypeCheck('string', value, false) ||
        (!hasMaxCharCountValidation && !hasMinCharCountValidation)
      ) {
        return;
      }

      if (
        hasMaxCharCountValidation &&
        (value as string).length > maxCharCountBounds
      ) {
        return formatMessage(errorMessages.maxCharLength, {
          maxBound: maxCharCountBounds,
        });
      } else if (
        hasMinCharCountValidation &&
        (value as string).length < minCharCountBounds
      ) {
        return formatMessage(errorMessages.minCharLength, {
          minBound: minCharCountBounds,
        });
      }
    };

    validate = (value: any): string => {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return (
        this.requiredValidation(value) ||
        this.maxLengthValidation(value) ||
        this.integerValidation(value) ||
        this.nonNegativeValidation(value) ||
        this.dateTimeFormat(value) ||
        this.numericBoundsValidation(value) ||
        this.charLengthValidation(value) ||
        this.selectionCountValidation(value)
      );
    };

    getShouldShowError = () => {
      const fieldName = this.getFieldName();
      const error = getIn(this.props.formik.errors, fieldName);
      const touched = getIn(this.props.formik.touched, fieldName);

      return !!(error && touched);
    };

    getError = () => {
      return getIn(this.props.formik.errors, this.getFieldName());
    };

    render() {
      const { formik, isReadOnly, intl, surveyItem, isEnabled, ...toPass } =
        this.props;

      const validate = isReadOnly
        ? (noop as (value: any) => string) // validation not needed if only viewing response
        : this.validate;

      return (
        <WrappedComponent
          validate={validate}
          error={this.getError()}
          shouldShowError={this.getShouldShowError()}
          isReadOnly={isReadOnly}
          surveyItem={surveyItem}
          isEnabled={isEnabled}
          {...(toPass as OtherProps)}
        />
      );
    }
  }

  return connect<any>(injectIntl(WithValidation));
};

export default withValidation;
